import { ReactElement, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Table } from '@digdir/designsystemet-react';
import { useLogExportQuery } from '../../../hooks/queries/safetyRounds/useOversightQueries';
import { useChecklistLanguage } from '../../../hooks/queries/safetyRounds/usePlanQueries';
import { longDate } from '../Plans/plansView';
import { filterFrom, logTime } from './oversightView';
import classes from './Oversight.module.css';

declare global {
  interface Window {
    __SAFETY_ROUND_LOG_READY__?: boolean;
  }
}

/** The log as a document: the filter, the number of events and every event in it */
export const LogPdfPage = (): ReactElement => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const filter = filterFrom(params);
  const reader = useChecklistLanguage();
  const language = params.get('language') === 'en' ? 'en' : params.get('language') ? 'nb' : reader;
  const { data, error } = useLogExportQuery(filter, language);
  const K = 'safetyRounds.oversight';
  const tl = (key: string, options?: Record<string, unknown>) => t(key, { lng: language === 'en' ? 'en' : 'no', ...options });

  // The PDF export waits for this, so every event is on the page
  useEffect(() => {
    if (data) window.__SAFETY_ROUND_LOG_READY__ = true;
  }, [data]);

  const period =
    filter.from || filter.to
      ? `${filter.from ? longDate(filter.from, language) : '…'} – ${filter.to ? longDate(filter.to, language) : '…'}`
      : tl(`${K}.pdf.allTime`);

  return (
    <article className={classes.document} data-safety-round-log>
      <header className={classes.documentHeader}>
        <Heading level={1} data-size="md">
          {tl(`${K}.pdf.title`)}
        </Heading>
        <dl className={classes.documentFacts}>
          <div>
            <dt>{tl(`${K}.pdf.filter`)}</dt>
            <dd>{tl(`${K}.groups.${filter.group}`)}</dd>
          </div>
          <div>
            <dt>{tl(`${K}.pdf.period`)}</dt>
            <dd>{period}</dd>
          </div>
          <div>
            <dt>{tl(`${K}.pdf.events`)}</dt>
            <dd>{data ? data.total : '…'}</dd>
          </div>
          <div>
            <dt>{tl(`${K}.pdf.made`)}</dt>
            <dd>{logTime(new Date().toISOString(), language)}</dd>
          </div>
        </dl>
      </header>
      {error && (
        <Alert data-color="danger">
          <Paragraph>{tl('safetyRounds.errors.loadMessage')}</Paragraph>
        </Alert>
      )}
      {data?.truncated && (
        <Alert data-color="warning" data-size="sm">
          <Paragraph data-size="sm">{tl(`${K}.pdf.truncated`, { shown: data.entries.length, total: data.total })}</Paragraph>
        </Alert>
      )}
      {data && (
        <Table data-size="sm" className={classes.documentTable}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>{tl(`${K}.pdf.time`)}</Table.HeaderCell>
              <Table.HeaderCell>{tl(`${K}.pdf.event`)}</Table.HeaderCell>
              <Table.HeaderCell>{tl(`${K}.pdf.plan`)}</Table.HeaderCell>
              <Table.HeaderCell>{tl(`${K}.pdf.by`)}</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {data.entries.map((entry) => (
              <Table.Row key={entry.id}>
                <Table.Cell className={classes.nowrap}>{logTime(entry.at, language)}</Table.Cell>
                <Table.Cell>
                  <strong>{entry.label}</strong>
                  {entry.summary && <div>{entry.summary}</div>}
                </Table.Cell>
                <Table.Cell>{entry.plan ?? '–'}</Table.Cell>
                <Table.Cell>{entry.actor ?? tl(`${K}.system`)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      <footer className={classes.documentFooter}>{tl(`${K}.pdf.footer`)}</footer>
    </article>
  );
};
